/**
 * SmsPreview renderer · iter303aj
 *
 * Runner card that previews an SMS message whose content is admin-editable
 * via Script Audit Studio (`app_settings.status_messages.{message_key}`).
 * Content is fetched fresh on mount and tokens are filled from captured_data.
 *
 * action.params:
 *   • message_key   — status_messages slot key
 *   • message_label — human-readable card title
 *   • prompt        — operator instruction text
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { MessageSquare, AlertTriangle, ExternalLink } from 'lucide-react';
import DOMPurify from 'dompurify';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

/**
 * Substitute `{token}` placeholders in an SMS body with values from
 * the passed-in `tokens` map. Token values are HTML-escaped BEFORE
 * substitution to prevent stored XSS (SEC-001 fix · 2026-02c).
 *
 * The template body itself is still rendered via
 * `dangerouslySetInnerHTML` so legitimate admin-authored markup
 * (line breaks etc.) works. Operator-supplied data (client names,
 * addresses etc.) can never reach the sink as raw HTML.
 */
function _escapeHTML(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function fillTokens(text, tokens) {
  if (!text) return '';
  return text.replace(/\{([A-Za-z0-9_]+)\}/g, (m, k) => {
    const v = tokens[k] || tokens[k.toLowerCase()];
    return v == null || v === '' ? m : _escapeHTML(v);
  });
}

export default function SmsPreview({ action, capturedData, onNext, onBack, onCancel, canBack }) {
  const params = action.params || {};
  const messageKey = params.message_key;
  const messageLabel = params.message_label || 'SMS Message';

  const [msg, setMsg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const { data } = await api.get(`/app-settings/status-messages/${messageKey}`);
      setMsg(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, [messageKey]);

  useEffect(() => { load(); }, [load]);

  const client = capturedData?.client_record || {};
  const prop = capturedData?.property_record || {};
  const propShort = [prop.property_address_num, prop.property_address_street].filter(Boolean).join(' ') || '(address)';
  const tokens = {
    name_first: client.name_first || '(name)',
    property_short_address: propShort,
    preferred_time_short: capturedData?.preferred_time_display
      || capturedData?.preferred_time_capture?.preferred_time_display
      || '(time)',
    appraiser_name: capturedData?.appraiser_pick?.name || '(appraiser)',
  };

  const filled = fillTokens(msg?.body, tokens);
  const charCount = filled.length;
  const overLimit = charCount > 160;
  const isDraft = !!msg?.is_draft;
  const mobile = client.phone_mobile || '';

  // iter303ak-fix22 · Track C v2 · Deep-link into Script Audit Studio.
  // Studio restores its variant/class from `script-audit-last-nav` in
  // localStorage on mount, so we pre-seed the target variant here then
  // open the Studio in a new tab. Every `msg_*` variant lives under the
  // `status_messages` class.
  const openInStudio = () => {
    if (!messageKey) return;
    try {
      localStorage.setItem('script-audit-last-nav', JSON.stringify({
        variant: `msg_${messageKey}`,
        active_class: 'status_messages',
      }));
    } catch { /* localStorage quota — non-blocking */ }
    window.open('/script-audit', '_blank', 'noopener,noreferrer');
  };

  return (
    <div data-testid="wf-sms-preview">
      <div className="bg-gradient-to-br from-purple-50 to-white border-l-4 border-purple-400 rounded-r-lg p-5 mb-5">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-purple-600 font-semibold mb-2">
          <MessageSquare size={12} /> {messageLabel}
          {isDraft && (
            <span data-testid="wf-sms-draft-badge"
              className="normal-case text-[10px] font-semibold text-amber-800 bg-amber-100 border border-amber-200 rounded px-1.5 py-0.5 ml-1 inline-flex items-center gap-1">
              <AlertTriangle size={10} /> DRAFT · needs audit
            </span>
          )}
        </div>
        <div className="text-slate-700 text-sm">{params.prompt || 'Preview the SMS and Continue when ready.'}</div>
      </div>

      {err && <div className="mb-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3">{err}</div>}

      {loading ? (
        <div className="p-6 text-sm text-slate-400 italic">Loading SMS content…</div>
      ) : (
        <div className="mb-5 max-w-md mx-auto">
          <div className="bg-slate-900 rounded-2xl p-4 shadow-lg" data-testid="wf-sms-bubble">
            <div className="text-[10px] text-slate-400 mb-1">To: {mobile || '(no mobile on file)'}</div>
            <div className="bg-purple-500 text-white text-sm px-4 py-3 rounded-2xl rounded-bl-sm leading-relaxed"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(filled) }} />
          </div>
          <div className="mt-2 text-xs text-slate-500 flex items-center justify-between">
            <span>{charCount} chars {overLimit && <span className="text-rose-500 font-semibold">(exceeds 160-char single-SMS limit — may split)</span>}</span>
            <span className="text-slate-400">SMS · standard rates</span>
          </div>
        </div>
      )}

      <div className="flex items-center gap-3">
        {canBack && (
          <button onClick={onBack} data-testid="wf-back-btn"
            className="px-4 py-2 text-slate-500 hover:text-slate-700">← Back</button>
        )}
        {onCancel && (
          <button onClick={onCancel} data-testid="wf-cancel-btn"
            className="px-4 py-2 text-rose-500 hover:text-rose-700 text-sm">Cancel</button>
        )}
        <span className="flex-1" />
        {/* iter303ak-fix22 · Edit template in Studio (opens new tab) */}
        {messageKey && (
          <button
            onClick={openInStudio}
            data-testid="wf-sms-edit-in-studio"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-white border-2 border-purple-500 text-purple-700 font-semibold rounded hover:bg-purple-50"
            title="Open Script Audit Studio to edit the SMS template — changes propagate to future runs">
            <ExternalLink size={14} /> Edit template in Studio
          </button>
        )}
        <button
          onClick={onNext}
          data-testid="wf-sms-continue-btn"
          className="px-6 py-2 bg-purple-600 text-white font-semibold rounded hover:bg-purple-700">
          Looks good — Continue →
        </button>
      </div>
    </div>
  );
}
